<template>
  <div class="form form-sm mx-auto">
    <h2 class="mb-4">Two-Factor Authentication</h2>
    <p class="lh-140">
      Two-factor authentication adds an additional layer of security to your
      account by requiring more than just a password to sign in.
    </p>

    <b-list-group>
      <b-list-group-item class="px-0 border-top-0 border-right-0 border-left-0">
        <strong>Email ({{ user.email }})</strong>
        <small class="mt-1 lh-160 d-block"
          >Email is enabled by default.<br />
          If an authenticator app is enabled, email is still available as a
          fallback option.</small
        >
      </b-list-group-item>
      <b-list-group-item
        class="px-0 border-right-0 border-left-0 border-bottom-0 d-flex align-items-center justify-content-between"
      >
        <strong>Authenticator App</strong>
        <base-switch
          v-model="enableAuthApp"
          @input="onAuthAppToggleSwitch"
          readonly
        />
      </b-list-group-item>
    </b-list-group>

    <template v-if="enableAuthApp">
      <a
        v-if="deviceVerified"
        @click.prevent="handleUseDiffApp"
        class="text-sm text-underline"
        href
        >Use a different authenticator app</a
      >
      <template v-else>
        <p class="font-weight-bold">
          Scan this QR code with an authenticator app.
        </p>
        <canvas ref="qrcRef"></canvas>

        <p class="mb-2">Enter the six-digit code from the authenticator app.</p>
        <InputCode v-model="otpValue" @input="handleCodeInput" class="mb-4" />

        <p class="mb-2">
          OR, enter this information manually into the authenticator app:
        </p>
        <base-input :value="otpQRSecret" input-classes="px-3" readonly />
      </template>
    </template>
  </div>
</template>

<script>
import Vue from 'vue'
import { defineComponent, ref, watch, nextTick } from '@vue/composition-api'
import { createVuexHelpers } from 'vue2-helpers'
import QRious from 'qrious'

import { OTPService } from '@services'

import InputCode from '@components/InputCode.vue'

export default defineComponent({
  name: 'Form2FASettings',
  components: {
    InputCode
  },
  setup() {
    const { useGetters, useActions, useMutations } = createVuexHelpers()
    const { user } = useGetters(['user'])
    const { setUser } = useActions(['setUser'])

    const qrcRef = ref()
    const deviceVerified = ref(user.value.device_2fa_enabled)
    const otpQRUrl = ref('')
    const otpQRSecret = ref('')
    const otpValue = ref('')

    // INFO: Auth app toggle logical concerns
    const enableAuthApp = ref(user.value.device_2fa_enabled)

    const onAuthAppToggleSwitch = async (status) => {
      if (status) await getDevices()
      else {
        if (deviceVerified.value) await removeDevice()

        otpQRUrl.value = ''
        otpQRSecret.value = ''
      }
    }

    const getDevices = async () => {
      const res = await OTPService.getDevices()

      if ('undefined' == typeof res) return

      otpQRUrl.value = res.data.data.url
      otpQRSecret.value = res.data.data.secret
    }

    const removeDevice = async () => {
      if (!deviceVerified.value) return

      const res = await OTPService.removeDevice()

      if ('undefined' == typeof res) return

      setUser({
        device_2fa_enabled: false
      })

      otpQRUrl.value = ''
      otpQRSecret.value = ''
      deviceVerified.value = false

      Vue.prototype.$toasted.show(res.data.data.message)
    }

    const handleUseDiffApp = async () => {
      otpQRUrl.value = ''
      otpQRSecret.value = ''

      await getDevices()

      deviceVerified.value = false
    }

    const handleCodeInput = async (token) => {
      if (6 > token.length) return

      const res = await OTPService.verifyDevice(token).catch((err) => {
        otpValue.value = ''

        Vue.prototype.$toasted.show(err.response.data.data[0], {
          type: 'error'
        })
      })

      if ('undefined' == typeof res) return

      setUser({
        device_2fa_enabled: true
      })

      deviceVerified.value = true
      otpValue.value = ''

      Vue.prototype.$toasted.show(res.data.data.message)
    }

    watch(
      () => otpQRUrl.value,
      async (newOtpQRUrl) => {
        await nextTick()

        new QRious({
          element: qrcRef.value,
          value: newOtpQRUrl,
          size: 200
        })
      }
    )

    return {
      user,
      qrcRef,

      deviceVerified,
      otpQRUrl,
      otpQRSecret,
      otpValue,

      enableAuthApp,
      onAuthAppToggleSwitch,

      getDevices,
      removeDevice,
      handleUseDiffApp,
      handleCodeInput
    }
  }
})
</script>

<style scoped>
canvas {
  margin: -1.125rem -1.125rem 0.5rem;
}

@media screen and (max-width: 767px) {
  .form {
    max-width: 360px;
  }
}
</style>
